import { auth, type AuthError } from '@atlaskit/outbound-auth-flow-client';
import { type ExternalUser } from '@atlaskit/user-picker';
import Fuse from 'fuse.js';
import {
	WINDOW_WIDTH,
	WINDOW_HEIGHT,
	JIRA_SLACK_URL,
	CONFLUENCE_SLACK_URL,
	MAX_USERS,
} from './constants';
import { DEFAULT_STARGATE_SERVICE_URL, EXUS_THIRDPARTY_PATH } from '../../clients/common';
import { type SlackWorkspace } from '../../types';

function getAuthUrl(serviceKey: string) {
	return `${DEFAULT_STARGATE_SERVICE_URL}${EXUS_THIRDPARTY_PATH}/connect?serviceKey=${serviceKey}`;
}

// kicks off outbound-auth oauth flow for third party providers in a popup window
export function beginThirdPartyOauth(
	serviceKey: string,
	callback: (success: boolean, error?: AuthError) => void,
): void {
	const url = getAuthUrl(serviceKey);
	const windowLeft = window.screen.width / 2 - WINDOW_WIDTH / 2;
	const windowTop = window.screen.height / 2 - WINDOW_HEIGHT / 2;
	const options =
		'menubar, width=' +
		WINDOW_WIDTH +
		', height=' +
		WINDOW_HEIGHT +
		', top=' +
		windowTop +
		', left=' +
		windowLeft;

	auth(url, options)
		.then(() => {
			if (callback) {
				callback(true);
			}
		})
		.catch((error: AuthError) => {
			if (callback) {
				callback(false, error);
			}
		});
}

function deleteFalsyValues<T extends {}>(obj: T) {
	(Object.keys(obj) as Array<keyof T>).filter((k) => !obj[k]).forEach((k) => delete obj[k]);
	return obj;
}

/**
 * Merges multiple lists of external users by the id, merging their sources (for merging results from the backend with slack users).
 */
export function mergeExternalUsers(userLists: ExternalUser[][]) {
	const usersById = userLists
		.reduce((flatUsersList: ExternalUser[], users: ExternalUser[]) => {
			flatUsersList.push(...users);

			return flatUsersList;
		}, [])
		.reduce<{
			[userId: string]: ExternalUser[];
		}>((result, currentValue) => {
			const key = currentValue.email || currentValue.id;
			if (!result[key]) {
				result[key] = [];
			}
			result[key].push(currentValue);
			return result;
		}, {});

	return Object.keys(usersById).map((userId) => {
		return usersById[userId].reduce((result: ExternalUser, currentValue: ExternalUser) => {
			const sources = [...new Set([...result.sources, ...currentValue.sources])];
			const mergedUser = {
				...deleteFalsyValues(result),
				...deleteFalsyValues(currentValue),
			};
			mergedUser.sources = sources;
			return mergedUser;
		});
	});
}

// export logic for managing slack workspaces
export const manageSlackWorkspaces = (
	isConnected: boolean,
	slackWorkspaces: SlackWorkspace[],
	isSlackConnectDialogOpen: boolean,
	openSlackConnectDialog: () => void,
	onSelectedSlackWorkspaceChange: (slackWorkspace: SlackWorkspace) => void,
	product: string,
	closeModal?: () => void,
): void => {
	if (slackWorkspaces.length === 0) {
		if (product === 'jira') {
			window.open(JIRA_SLACK_URL);
			if (closeModal) {
				closeModal();
			}
		} else if (product === 'confluence') {
			window.open(CONFLUENCE_SLACK_URL);
			if (closeModal) {
				closeModal();
			}
		}
	} else if (!isConnected && slackWorkspaces.length === 1) {
		onSelectedSlackWorkspaceChange(slackWorkspaces[0]);
	} else if (!isSlackConnectDialogOpen) {
		openSlackConnectDialog();
	}
};

interface MergeAndSearchExternalUsersArguments {
	/**
	 * Users provided by Invite People consumers.
	 */
	providedAdditionalUsers: ExternalUser[];
	/**
	 * Users from the 3rd party integrations supported by EXUS that the user has connected.
	 */
	connectedThirdPartyUsers: ExternalUser[];
	searchText: string;
}

/**
 * Merges multiple search results (`connectedThirdPartyUsers`, `providedAdditionalUsers`)
 * and returns the the appropriate result considering the received `searchText` and the `MAX_USERS`.
 */
export const mergeAndSearchExternalUsers = (
	data: MergeAndSearchExternalUsersArguments,
): ExternalUser[] => {
	const { providedAdditionalUsers, connectedThirdPartyUsers, searchText } = data;
	const additionalUsersWithEmail = providedAdditionalUsers.filter((user) => user.email);
	const additionalUsersWithoutEmail = providedAdditionalUsers.filter((user) => !user.email);
	const users = mergeExternalUsers([additionalUsersWithEmail, connectedThirdPartyUsers]);
	const fuse = fuseFactory(users);
	const searchResult = fuse.search(searchText).map((result) => result.item);

	const searchResultWithAdditionalUsers = [
		// Unable to include additional users in search as emails are filtered via the API
		...additionalUsersWithoutEmail.slice(
			0,
			Math.min(additionalUsersWithoutEmail.length, MAX_USERS),
		),
		...searchResult,
	];

	return searchResultWithAdditionalUsers.slice(
		0,
		Math.min(searchResultWithAdditionalUsers.length, MAX_USERS),
	);
};
export function fuseFactory<T>(items: T[]) {
	return new Fuse(items, {
		keys: ['displayName', 'name', 'email', 'publicName'],
		threshold: 0.2,
	});
}
